/** @jsxImportSource react */
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { toast } from '../components/ui/Toast';
import { Mail, ArrowLeft } from 'lucide-react';

import { api } from '../services/api';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/Button';

const ForgotPasswordPage: React.FC = () => {
    const [email, setEmail] = React.useState('');
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const { t } = useTranslation();

    const mutation = useMutation({
        mutationFn: (email: string) => api.sendPasswordResetEmail(email),
        onSuccess: () => {
            setIsSubmitted(true);
        },
        onError: (error: any) => {
            // For security reasons, don't reveal if the user doesn't exist.
            // Just show the success message.
            if (error.code === 'auth/user-not-found') {
                setIsSubmitted(true);
            } else {
                toast.error(t('unexpectedError'));
            }
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        mutation.mutate(email);
    };

    return (
        <div className="relative min-h-screen w-full bg-secondary-50 flex items-center justify-center p-4 overflow-hidden">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            
            <div className="relative z-10 w-full max-w-md mx-auto bg-white p-8 sm:p-12 shadow-2xl rounded-xl border border-secondary-100 animate-fade-in">
                {isSubmitted ? (
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <div className="h-16 w-16 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-600">
                                <Mail size={32} />
                            </div>
                        </div>
                        <h2 className="text-2xl font-bold text-secondary-900 mb-2">{t('checkYourEmail')}</h2>
                        <p className="text-secondary-600">
                            {t('passwordResetLinkSent', { email })}
                        </p>
                        <Link to="/login" className="mt-8 inline-block">
                           <Button variant="outline">{t('backToLogin')}</Button>
                        </Link>
                    </div>
                ) : (
                    <div>
                        <div className="text-center mb-8">
                            <h2 className="text-3xl font-bold text-secondary-900 mb-2">{t('forgotPassword')}</h2>
                            <p className="text-secondary-500">{t('forgotPasswordDesc')}</p>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-secondary-700 text-sm font-medium mb-2" htmlFor="email">
                                    {t('email_label')}
                                </label>
                                <div className="relative">
                                    <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                                        <Mail size={18} className="text-secondary-400" />
                                    </div>
                                    <input
                                        id="email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={t('emailPlaceholder')}
                                        required
                                        className={`w-full py-3 rounded-lg border border-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 ps-10 pe-4`}
                                    />
                                </div>
                            </div>
                            <Button type="submit" fullWidth disabled={mutation.isPending} isLoading={mutation.isPending}>
                                {t('sendResetLink')}
                            </Button>
                        </form>
                        <div className="text-center mt-6">
                            <Link
                                to="/login"
                                className="font-medium text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center justify-center gap-1"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                {t('backToLogin')}
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
export default ForgotPasswordPage;